import styled, { css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { WhatsAppIcon } from '@womcorp/wom-miwom-ui-kit/dist/@theme/assets/svg/icons';

const desktop = '(min-width: 769px)';
const mobile = '(max-width: 768px)';

const showLinks = keyframes`
  0% {
    max-height: 47px;
  }

  100% {
    max-height: 400px;
  }
`;

const hideLinks = keyframes`
  0% {
    max-height: 400px;
  }

  100% {
    max-height: 47px;
  }
`;

const setContainerStyles = ({ tag }) => {
  switch (tag) {
    case 'contact':
      return css`
        max-height: unset;
        margin-bottom: 32px;
        grid-row: 2;

        @media ${desktop} {
          margin-bottom: unset;
        }
      `;

    default:
      return css`
        max-height: 47px;
        overflow: hidden;
        border-bottom: 1px solid rgba(255, 255, 255, 0.3);
        grid-row: auto;

        @media ${desktop} {
          border-bottom: unset;
          overflow: visible;
        }
      `;
  }
};

const setActive = ({ active, tag }) => {
  if (active && tag !== 'contact') {
    return css`
      @media ${mobile} {
        animation: ${showLinks} 1s ease;
        animation-fill-mode: forwards;

        svg {
          transform: rotate(45deg);
        }
      }
    `;
  }

  if (!active && tag !== 'contact') {
    return css`
      @media ${mobile} {
        animation: ${hideLinks} 1s ease;
        animation-fill-mode: forwards;
      }
    `;
  }

  return null;
};

const ItemContainer = styled.div`
  ${setContainerStyles}
  ${setActive}

  @media ${desktop} {
    display: flex;
    flex-direction: column;
    grid-row: 2;
    flex-shrink: 0;
    min-width: 130px;
    max-width: 260px;
    max-height: unset;
    border-bottom: unset;
  }
`;

const ItemHeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 47px;

  @media ${desktop} {
    display: block;
    height: auto;
  }

  svg {
    transition: transform 0.3s ease;

    @media ${desktop} {
      display: none;
    }
  }
`;

const ItemTitleStyled = styled.h3`
  font-family: 'cera';
  font-size: 18px;
  line-height: 22px;
  font-weight: 500;

  @media ${desktop} {
    font-family: 'cera';
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 34px;
    font-weight: 700;
  }
`;

const ContactTitleStyled = styled(ItemTitleStyled)`
  margin: 0 auto;

  @media ${desktop} {
    margin: 0 auto 34px;
  }
`;

const ListStyled = styled.ul`
  padding-bottom: 25px;

  @media ${desktop} {
    padding-bottom: unset;
  }

  > li {
    :not(:last-child) {
      margin-bottom: 20px;
    }
  }
`;

const ListLinkStyled = styled.a`
  width: 100%;
  font-size: 13px;
  color: #e4e4e4;
  font-weight: 400;
  position: relative;
  text-decoration: none;
  display: block;
  transform: translateX(0);
  transition: all 0.3s ease;

  :before {
    content: '>';
    position: absolute;
    left: -15px;
    height: 20px;
    width: 100px;
    top: 0;
    opacity: 0;
    transform: translateX(-20px);
    transition: all.3s ease;
  }

  :hover {
    transform: translateX(20px);
    color: white;
    cursor: pointer;

    :before {
      opacity: 1;
      transform: translateX(-2px);
    }
  }
`;

const PhoneGroupStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  :not(:last-child) {
    margin-top: 25px;
    margin-bottom: 20px;
  }

  @media ${desktop} {
    display: block;

    :not(:last-child) {
      margin-top: unset;
      margin-bottom: 40px;
    }
  }

  > h4 {
    font-family: 'cera';
    font-size: 14px;
    line-height: 18px;
    color: #afa6b7;
    margin-bottom: 8px;
    font-weight: 400;

    @media ${desktop} {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
    }
  }
`;

const ContactNumberStyled = styled.div`
  border: 1px solid #7c6c8a;
  border-radius: 20px;
  padding: 10px 15px;
  width: 200px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  @media ${desktop} {
    display: inline-flex;
    align-items: center;
    border: unset;
    border-radius: unset;
    padding: unset;
    width: unset;
    justify-content: unset;
  }

  > svg {
    height: 14px;
    width: 14px;
    margin-right: 10px;
    fill: ${props => props.phoneColor};
  }

  > a {
    color: #fff;
    font-family: 'cerapro';
    font-size: 16px;
    line-height: 20px;
    font-weight: 400;
    outline: none;
    text-decoration: inherit;

    @media ${desktop} {
      font-size: 18px;
      font-weight: 700;
    }
  }
`;

const WhatsappIconStyled = styled(WhatsAppIcon)`
  && {
    min-height: 16px;
    width: 16px;

    path {
      fill: #33cc9e;
    }
  }
`;

ContactNumberStyled.defaultProps = {
  phoneColor: '#FFF',
};

ContactNumberStyled.propTypes = {
  phoneColor: PropTypes.string,
};

export {
  ListLinkStyled,
  ListStyled,
  ItemContainer,
  ItemHeaderStyled,
  ContactNumberStyled,
  PhoneGroupStyled,
  WhatsappIconStyled,
  ItemTitleStyled,
  ContactTitleStyled,
};
